/**
 * `<TopDownEditor>` — the docked editor for a TopDown screen: tabs **Sector ·
 * Industry · Symbols** (`docs/features/topdown.md`, mock §4).
 *
 * Sector and Industry belong to the TopDown screen: an ordered list of columns,
 * each a ScanScript expression **evaluated on the group's index bars** (or a
 * plain bar field — `close`, `changePercentCc` …). There is no aggregation to
 * pick: a level's number is never a sum or mean of its members (owner,
 * 2026-10-16). Symbols is the base screen: pick which one, and open it to edit
 * its own columns — this screen holds no copy of them.
 *
 * Weighting: Equal or Cap — cap from each member's diluted share count as
 * filed, uncapped, reweighted daily; a member with no count weighs the average
 * (owner, 2026-10-16). It moves the chart's index and any column computed on it;
 * Δ, % and fM are member sums either way.
 */

import { useState, type CSSProperties } from "react";
import type { ScreenerColumn, ScreenerTopDown } from "@qufolio/model";
import type { CompileExpr } from "@qufolio/screener-grid";

import { isOwn, type Screen } from "../lib/screens";
import { LEVEL_KEYS, type LevelKey } from "../lib/topdown";

/** Plain fields a level may show without an expression (`qf/topdown.BAR_FIELDS`). */
export const BAR_FIELDS = [
  "open", "high", "low", "close", "volume", "prevClose",
  "change", "changePercent", "changeCc", "changePercentCc",
] as const;

type Tab = LevelKey | "symbols";
const TAB_LABEL: Record<Tab, string> = { sector: "Sector", industry: "Industry", symbols: "Symbols" };

export interface TopDownEditorProps {
  topDown: ScreenerTopDown;
  /** The TopDown screen itself — editable only when it is your own. */
  screen?: Screen;
  screens: readonly Screen[];
  compileExpr?: CompileExpr;
  onChange: (next: ScreenerTopDown) => void;
  /** Switch the panel to the base screen, to edit its own columns. */
  onOpenBase: (key: string) => void;
  onClose?: () => void;
}

export function TopDownEditor({ topDown, screen, screens, compileExpr, onChange, onOpenBase, onClose }: TopDownEditorProps) {
  const [tab, setTab] = useState<Tab>("sector");
  const editable = !screen || isOwn(screen);
  return (
    <div style={ROOT}>
      <div style={TABS} role="tablist">
        {(["sector", "industry", "symbols"] as Tab[]).map((t) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t}
            style={tab === t ? { ...TAB, ...TAB_ON } : TAB} onClick={() => setTab(t)}>
            {TAB_LABEL[t]}
          </button>
        ))}
        {onClose && (
          <button type="button" aria-label="Close editor" style={{ ...TAB, marginLeft: "auto" }} onClick={onClose}>
            ×
          </button>
        )}
      </div>
      <div style={BODY}>
        {tab === "symbols" ? (
          <SymbolsTab topDown={topDown} screens={screens} editable={editable} onChange={onChange} onOpenBase={onOpenBase} />
        ) : (
          <LevelTab
            key={tab}
            level={tab}
            topDown={topDown}
            editable={editable}
            compileExpr={compileExpr}
            onChange={onChange}
          />
        )}
        {!editable && <div style={NOTE}>This TopDown is published by its group; its tabs are read-only.</div>}
      </div>
    </div>
  );
}

function LevelTab({
  level,
  topDown,
  editable,
  compileExpr,
  onChange,
}: {
  level: LevelKey;
  topDown: ScreenerTopDown;
  editable: boolean;
  compileExpr?: CompileExpr;
  onChange: (next: ScreenerTopDown) => void;
}) {
  const columns = topDown.levels.find((l) => l.key === level)?.columns ?? [];
  const [text, setText] = useState("");
  const [header, setHeader] = useState("");
  const [error, setError] = useState<string | null>(null);

  const write = (next: ScreenerColumn[]) => {
    const levels = LEVEL_KEYS.map((key) => {
      const held = topDown.levels.find((l) => l.key === key);
      return key === level ? { key, columns: next } : (held ?? { key });
    });
    onChange({ ...topDown, levels });
  };
  const move = (i: number, by: number) => {
    const next = [...columns];
    const [c] = next.splice(i, 1);
    if (!c) return;
    next.splice(Math.max(0, Math.min(next.length, i + by)), 0, c);
    write(next);
  };
  const add = async () => {
    const source = text.trim();
    if (!source) return;
    setError(null);
    if ((BAR_FIELDS as readonly string[]).includes(source)) {
      write([...columns, { field: source, header: header.trim() || source, type: "number", precision: 2 }]);
    } else {
      if (!compileExpr) return setError("No expression compiler is configured.");
      const result = await compileExpr(source);
      if (!result.ok) return setError(result.error.message);
      const field = uniqueField(columns, source);
      write([
        ...columns,
        {
          field,
          header: header.trim() || source,
          type: "number",
          precision: 2,
          computed: { expression: result.expression, source: result.source, requires: result.requires },
        },
      ]);
    }
    setText("");
    setHeader("");
  };

  return (
    <div>
      <div style={ROW}>
        <span style={MUTED}>Weighting</span>
        {(["equal", "cap"] as const).map((w) => {
          const on = (topDown.weighting ?? "equal") === w;
          return (
            <button
              key={w}
              type="button"
              disabled={!editable}
              aria-pressed={on}
              style={on ? { ...CHIP, ...CHIP_ON } : CHIP}
              title={w === "cap"
                ? "Weigh each member by its market value: diluted shares as filed × price, every day"
                : "Every member counts the same"}
              onClick={() => onChange({ ...topDown, weighting: w })}
            >
              {w === "cap" ? "Cap" : "Equal"}
            </button>
          );
        })}
      </div>
      <p style={NOTE}>
        Δ, %, fM2, fM5 and fM13 add up the {level}'s members, as QuantVitals does: Δ is their sum, % is
        their change over their previous close, fM is their average. Any other column (RSI) is computed on
        the {level}'s index, its own series of bars.
      </p>
      <table style={TABLE}>
        <tbody>
          {columns.map((c, i) => (
            <tr key={c.field} style={c.hide ? { opacity: 0.55 } : undefined}>
              {/* Show — the screen editor's own tick: unticked stays listed, hidden. */}
              <td style={{ ...TD, width: 24 }}>
                <input
                  type="checkbox"
                  checked={!c.hide}
                  disabled={!editable}
                  aria-label={`Show ${c.header ?? c.field}`}
                  onChange={() =>
                    write(columns.map((x, j) => (j === i ? (x.hide ? (({ hide: _h, ...rest }) => rest)(x) : { ...x, hide: true }) : x)))
                  }
                />
              </td>
              <td style={TD}>{c.header ?? c.field}</td>
              <td style={{ ...TD, ...MUTED }}>{c.computed?.source ?? c.field}</td>
              {editable && (
                <td style={{ ...TD, textAlign: "right", whiteSpace: "nowrap" }}>
                  <button type="button" style={BTN} aria-label={`Move ${c.field} up`} onClick={() => move(i, -1)}>↑</button>
                  <button type="button" style={BTN} aria-label={`Move ${c.field} down`} onClick={() => move(i, 1)}>↓</button>
                  <button type="button" style={BTN} aria-label={`Remove ${c.field}`}
                    onClick={() => write(columns.filter((_, j) => j !== i))}>✕</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {editable && (
        <div style={{ ...ROW, marginTop: 8 }}>
          <input style={INPUT} placeholder="rsi(c, 14) or changePercentCc" value={text}
            aria-label="Column expression" onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") void add(); }} />
          <input style={{ ...INPUT, width: 70 }} placeholder="Header" value={header}
            aria-label="Column header" onChange={(e) => setHeader(e.target.value)} />
          <button type="button" style={BTN} onClick={() => void add()}>Add</button>
        </div>
      )}
      {error && <div style={{ ...NOTE, color: "var(--qf-negative)" }}>{error}</div>}
    </div>
  );
}

function SymbolsTab({
  topDown,
  screens,
  editable,
  onChange,
  onOpenBase,
}: {
  topDown: ScreenerTopDown;
  screens: readonly Screen[];
  editable: boolean;
  onChange: (next: ScreenerTopDown) => void;
  onOpenBase: (key: string) => void;
}) {
  // Any ordinary screen can be the base; a TopDown over a TopDown is not one.
  const bases = screens.filter((s) => !s.topDown);
  return (
    <div>
      <p style={NOTE}>
        The Symbols grid is another screen, referenced rather than copied: edit that screen and this one
        follows. Its rows — universe and filter — are the members every sector and industry is built from.
      </p>
      <div style={ROW}>
        <span style={MUTED}>Screen</span>
        <select value={topDown.base} disabled={!editable} aria-label="Symbols screen"
          onChange={(e) => {
            // A new base: the ticked names may not exist under it.
            const { ticks: _t, ...rest } = topDown;
            onChange({ ...rest, base: e.target.value });
          }}>
          {!bases.some((s) => s.key === topDown.base) && <option value={topDown.base}>{topDown.base}</option>}
          {bases.map((s) => (
            <option key={s.key} value={s.key}>{s.name}</option>
          ))}
        </select>
        <button type="button" style={BTN} onClick={() => onOpenBase(topDown.base)}>Edit that screen ↗</button>
      </div>
    </div>
  );
}

/** A field id for a new computed column, unique in the level: `td` + a slug. */
function uniqueField(columns: readonly ScreenerColumn[], source: string): string {
  const slug = source.replace(/[^A-Za-z0-9]+/g, " ").trim().split(" ")
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join("").slice(0, 24) || "col";
  const stem = `td${slug.charAt(0).toUpperCase()}${slug.slice(1)}`;
  let field = stem;
  for (let n = 2; columns.some((c) => c.field === field); n += 1) field = `${stem}${n}`;
  return field;
}

const ROOT: CSSProperties = { display: "flex", flexDirection: "column", flex: "1 1 auto", minHeight: 0, background: "var(--qf-surface)", color: "var(--qf-text)", fontSize: 12 };
const TABS: CSSProperties = { display: "flex", gap: 2, padding: "6px 8px 0", borderBottom: "1px solid var(--qf-border)" };
const TAB: CSSProperties = { font: "inherit", padding: "5px 12px", border: "1px solid transparent", borderBottom: 0, borderRadius: "6px 6px 0 0", background: "transparent", color: "var(--qf-text-muted)", cursor: "pointer" };
const TAB_ON: CSSProperties = { background: "var(--qf-background)", borderColor: "var(--qf-border)", color: "var(--qf-text)", marginBottom: -1 };
const BODY: CSSProperties = { padding: "10px 12px", overflow: "auto", flex: "1 1 auto", background: "var(--qf-background)" };
const ROW: CSSProperties = { display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" };
const MUTED: CSSProperties = { color: "var(--qf-text-muted)" };
const NOTE: CSSProperties = { color: "var(--qf-text-muted)", fontSize: 11, margin: "8px 0" };
const CHIP: CSSProperties = { fontSize: 11, padding: "1px 7px", borderRadius: 5, border: "1px solid var(--qf-border)" };
const CHIP_ON: CSSProperties = { borderColor: "var(--qf-accent)", color: "var(--qf-accent)" };
const TABLE: CSSProperties = { borderCollapse: "collapse", width: "100%" };
const TD: CSSProperties = { padding: "3px 6px", borderBottom: "1px solid var(--qf-border)" };
const BTN: CSSProperties = { font: "inherit", fontSize: 11, padding: "1px 6px", marginLeft: 2, borderRadius: 4, border: "1px solid var(--qf-border)", background: "var(--qf-surface-alt)", color: "var(--qf-text)", cursor: "pointer" };
const INPUT: CSSProperties = { font: "inherit", fontSize: 12, padding: "2px 6px", flex: "1 1 160px", minWidth: 0, borderRadius: 4, border: "1px solid var(--qf-border)", background: "var(--qf-surface-alt)", color: "var(--qf-text)" };
